import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Seo from '../shared/Seo';
import { useAuth } from '../features/auth';
import { useLocker } from '../features/locker';
import { useToast } from '../features/toast';
import {
  logLockerShowcaseOpened,
  logLockerShowcaseItemSelected,
  logLockerShowcaseExportClicked,
  logLockerShowcaseExportSuccess,
  logLockerShowcaseExit,
} from '../services/analytics';

const FALLBACK_IMAGE = '/assets/ponpon-logo.svg';
const SHELF_IMAGE_SRC = '/assets/shelf.png';
const SHELF_BACKGROUND_SRC = '/assets/shelf_background.png';
const WATERMARK_SRC = '/assets/ponpon-logo.svg';

// Shelf fixed to 9 slots (3x3 grid)
const SHELF_SLOT_COUNT = 9;

// Export format: 4:5 (taller portrait)
const EXPORT_FORMATS = [
  { id: '4:5', label: '4:5', width: 1080, height: 1350, aspectClass: 'aspect-[4/5]' },
];

const EXPORT_SCALE = 1; // base export at 1080px width

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    if (!src) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });

const renderShowcaseToCanvas = async (canvas, options) => {
  const { width, height, slotImages } = options;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  canvas.width = width;
  canvas.height = height;

  // Load shared assets: background + shelf (geometry only) + watermark + per-slot images
  const [backgroundImage, shelfImage, watermarkImage, ...itemImages] = await Promise.all([
    loadImage(SHELF_BACKGROUND_SRC),
    loadImage(SHELF_IMAGE_SRC),
    loadImage(WATERMARK_SRC),
    ...slotImages.map((src) => loadImage(src).catch(() => null)),
  ]);

  // Background: updated asset; fallback to gradient
  if (backgroundImage && backgroundImage.width > 0 && backgroundImage.height > 0) {
    const scale = Math.max(width / backgroundImage.width, height / backgroundImage.height);
    const drawWidth = backgroundImage.width * scale;
    const drawHeight = backgroundImage.height * scale;
    const drawX = (width - drawWidth) / 2;
    const drawY = (height - drawHeight) / 2;
    ctx.drawImage(backgroundImage, drawX, drawY, drawWidth, drawHeight);
  } else {
    const bg = ctx.createLinearGradient(0, 0, 0, height);
    bg.addColorStop(0, '#ffffff');
    bg.addColorStop(0.5, '#e5e7eb');
    bg.addColorStop(1, '#f3f4f6');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, width, height);
  }

  // Shelf geometry for item placement only; do not draw shelf.png
  const shelfDrawWidth = width * 0.8;
  const shelfDrawHeight =
    shelfImage && shelfImage.width > 0
      ? (shelfDrawWidth * shelfImage.height) / shelfImage.width
      : width * 0.05;
  const shelfX = (width - shelfDrawWidth) / 2;
  const topShelfY = height * 0.32;
  const middleShelfY = height * 0.56;
  const bottomShelfY = height * 0.8;

  // Draw items on shelves
  const slotWidth = shelfDrawWidth / 3;
  const maxItemHeight = height * 0.2;
  const maxItemWidth = slotWidth * 0.7;
  const itemOffsetAboveShelf = 0; // keep item base flush with shelf surface

  const drawItemInSlot = (index, shelfY) => {
    const image = itemImages[index];
    if (!image) return;

    const col = index % 3;
    const centerX = shelfX + slotWidth * col + slotWidth / 2;
    // Approximate the actual top surface of the 3D shelf (no shelf drawn; background only)
    const shelfSurfaceY = shelfY + shelfDrawHeight * 0.58;
    const baseY = shelfSurfaceY;

    const scale = Math.min(
      maxItemWidth / image.width,
      maxItemHeight / image.height,
    );
    const drawWidth = image.width * scale;
    const drawHeight = image.height * scale;
    const drawX = centerX - drawWidth / 2;
    const drawY = baseY - drawHeight - itemOffsetAboveShelf;

    ctx.save();
    ctx.shadowColor = 'rgba(15,23,42,0.55)';
    ctx.shadowBlur = 30;
    ctx.shadowOffsetY = 24;
    ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);
    ctx.restore();
  };

  // Slots 0–2: top shelf, 3–5: middle shelf, 6–8: bottom shelf
  for (let i = 0; i < 3; i += 1) {
    drawItemInSlot(i, topShelfY);
  }
  for (let i = 3; i < 6; i += 1) {
    drawItemInSlot(i, middleShelfY);
  }
  for (let i = 6; i < 9; i += 1) {
    drawItemInSlot(i, bottomShelfY);
  }

  // Watermark bottom-right
  if (watermarkImage) {
    const margin = width * 0.035;
    const wmWidth = width * 0.18;
    const wmHeight = (wmWidth * watermarkImage.height) / watermarkImage.width;
    const wmX = width - wmWidth - margin;
    const wmY = height - wmHeight - margin;
    ctx.save();
    ctx.globalAlpha = 0.6;
    ctx.drawImage(watermarkImage, wmX, wmY, wmWidth, wmHeight);
    ctx.restore();
  }
};

// CHANGED: Locker Showcase builder (Phase 2) – mobile-first layout & item selection
const LockerShowcasePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { resolveLockerItems } = useLocker();
  const previewRef = useRef(null);
  const latestSlotAssignmentsRef = useRef([]);

  const allLockerItems = useMemo(() => {
    if (!user) {
      return [];
    }
    // Prefer full purchase history over current locker contents
    if (Array.isArray(user.purchases) && user.purchases.length > 0) {
      return user.purchases;
    }
    // Fallback to existing locker resolver for older accounts
    return resolveLockerItems?.(user) || [];
  }, [resolveLockerItems, user]);

  // CHANGED: normalize + sort locker entries (newest first) for stable grouping/selection
  const sortedLockerItems = useMemo(() => {
    if (!Array.isArray(allLockerItems)) {
      return [];
    }
    return [...allLockerItems].sort((a, b) => {
      const timeA = a?.timestamp ? new Date(a.timestamp).getTime() : 0;
      const timeB = b?.timestamp ? new Date(b.timestamp).getTime() : 0;
      return timeB - timeA;
    });
  }, [allLockerItems]);

  // CHANGED: In "My Gachapon", show every item the user actually has.
  // Duplicates should appear as duplicates (no grouping).
  const selectableItems = useMemo(() => {
    return sortedLockerItems
      .map((entry, index) => {
        const prizeName = entry?.prize?.name || entry?.name || null;
        const prizeImg =
          typeof entry?.prize?.imageUrl === 'string' && entry.prize.imageUrl.trim().length > 0
            ? entry.prize.imageUrl.trim()
            : typeof entry?.imageUrl === 'string' && entry.imageUrl.trim().length > 0
            ? entry.imageUrl.trim()
            : null;
        if (!prizeName || !prizeImg) {
          return null;
        }
        const entryId = entry?.id || null;
        const prizeId = entry?.prize?.id || entry?.prizeId || null;
        const ts = entry?.timestamp || '';
        // Prefer server-provided entry.id; otherwise use a stable-ish composite key.
        const key = entryId ? `entry:${entryId}` : `fallback:${String(prizeId || '')}|${String(ts)}|${index}`;
        return { key, entry, prizeName, prizeImg };
      })
      .filter(Boolean);
  }, [sortedLockerItems]);

  // CHANGED: layout-first approach → slotAssignments holds one groupKey per shelf slot
  // CHANGED: slotAssignments now holds one selectableItem.key per slot (not grouped keys)
  const [slotAssignments, setSlotAssignments] = useState(() => Array(SHELF_SLOT_COUNT).fill(null));
  const [activeSlotIndex, setActiveSlotIndex] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  // CHANGED: in-page modal for exported image (iOS / fallback); no popup = more stable
  const [exportedImageDataUrl, setExportedImageDataUrl] = useState(null);
  const { showToast } = useToast();

  // CHANGED: prevent assigning the same exact item into multiple slots
  const assignedKeys = useMemo(() => new Set(slotAssignments.filter(Boolean)), [slotAssignments]);
  // CHANGED: hide items that are already used in the preview shelf
  const availableSelectableItems = useMemo(
    () => selectableItems.filter((item) => !assignedKeys.has(item.key)),
    [assignedKeys, selectableItems],
  );

  const handleSlotPick = (slotIndex, itemKey) => {
    const next = [...slotAssignments];
    next[slotIndex] = itemKey;
    setSlotAssignments(next);
    logLockerShowcaseItemSelected(
      user?.uid,
      next.filter(Boolean).length,
      next.filter(Boolean),
    );
    setActiveSlotIndex(null);
  };

  const handleSlotClick = (slotIndex) => {
    const hasItem = !!slotAssignments[slotIndex];
    // If this slot already has an item and is currently active, a second tap clears it
    if (hasItem && activeSlotIndex === slotIndex) {
      const next = [...slotAssignments];
      next[slotIndex] = null;
      setSlotAssignments(next);
      logLockerShowcaseItemSelected(
        user?.uid,
        next.filter(Boolean).length,
        next.filter(Boolean),
      );
      setActiveSlotIndex(null);
      return;
    }
    // Otherwise just select this slot
    setActiveSlotIndex(slotIndex);
  };

  const selectedItems = useMemo(() => {
    return slotAssignments
      .filter(Boolean)
      .map((key) => selectableItems.find((item) => item.key === key)?.entry)
      .filter(Boolean);
  }, [selectableItems, slotAssignments]);

  const currentFormat = EXPORT_FORMATS[0];

  // Keep latest slotAssignments in a ref for exit analytics
  useEffect(() => {
    latestSlotAssignmentsRef.current = slotAssignments;
  }, [slotAssignments]);

  // Log open / exit events
  useEffect(() => {
    const startTime = Date.now();
    logLockerShowcaseOpened(user?.uid);
    return () => {
      const timeSpentSeconds = Math.round((Date.now() - startTime) / 1000);
      const filled = (latestSlotAssignmentsRef.current || []).filter(Boolean);
      logLockerShowcaseExit(user?.uid, timeSpentSeconds, filled.length);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // CHANGED: No popup; iOS shows image in same-page modal (long-press to save). More stable.
  const handleDownload = () => {
    if (!previewRef.current || selectedItems.length === 0 || isExporting) {
      return;
    }

    const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
    const isIOS = /iPhone|iPad|iPod/i.test(ua);

    logLockerShowcaseExportClicked(user?.uid, currentFormat.id);
    setIsExporting(true);

    void (async () => {
      let fileSize;
      try {
        const canvas = document.createElement('canvas');
        const targetWidth = currentFormat.width * EXPORT_SCALE;
        const targetHeight = currentFormat.height * EXPORT_SCALE;

        const slotImages = slotAssignments.map((key) => {
          if (!key) {
            return null;
          }
          const item = selectableItems.find((x) => x.key === key);
          const entry = item?.entry;
          const raw =
            (entry?.prize && entry.prize.imageUrl && entry.prize.imageUrl.trim().length > 0
              ? entry.prize.imageUrl
              : entry?.imageUrl) || null;
          return raw || FALLBACK_IMAGE;
        });

        await renderShowcaseToCanvas(canvas, {
          width: targetWidth,
          height: targetHeight,
          slotImages,
        });

        if (isIOS) {
          const blob = await new Promise((resolve, reject) => {
            canvas.toBlob((b) => {
              if (b) resolve(b);
              else reject(new Error('Failed to export canvas blob'));
            }, 'image/png');
          });
          fileSize = blob.size;

          const supportsShare =
            typeof navigator !== 'undefined' &&
            typeof navigator.canShare === 'function' &&
            typeof navigator.share === 'function';

          let shared = false;
          if (supportsShare) {
            try {
              const file = new File(
                [blob],
                `ponpon-showcase-${currentFormat.id.replace(':', '-')}.png`,
                { type: 'image/png' },
              );
              if (navigator.canShare({ files: [file] })) {
                await navigator.share({
                  files: [file],
                  title: 'PonDisplay | PonPon!',
                  text: 'Check out my PonDisplay from ponpon.app ✨',
                });
                shared = true;
              }
            } catch (shareError) {
              // eslint-disable-next-line no-console
              console.warn('Web Share failed, falling back to in-page image', shareError);
            }
          }

          if (!shared) {
            const dataUrl = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = () => reject(new Error('FileReader failed'));
              reader.readAsDataURL(blob);
            });
            setExportedImageDataUrl(dataUrl);
          }
        } else {
          // Other browsers: use Blob download to trigger a real file save
          const blob = await new Promise((resolve, reject) => {
            canvas.toBlob((b) => {
              if (b) resolve(b);
              else reject(new Error('Failed to export canvas blob'));
            }, 'image/png');
          });
          fileSize = blob.size;
          const blobUrl = URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = `ponpon-showcase-${currentFormat.id.replace(':', '-')}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
        }

        logLockerShowcaseExportSuccess(user?.uid, currentFormat.id, fileSize);
      } catch (error) {
        console.error('Failed to export showcase image', error);
        showToast({ message: 'Could not create image. Please try again.', tone: 'error' });
      } finally {
        setIsExporting(false);
      }
    })();
  };

  return (
    <>
    <div className="flex flex-col gap-8">
      <Seo
        title="PonDisplay | PonPon!"
        description="Create a share-ready showcase image from your Locker items."
        canonical="https://ponpon.app/locker/showcase"
        noindex={true}
      />
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold text-ponpon-navy sm:text-4xl">
            PonDisplay
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/locker')}
            className="inline-flex items-center gap-2 rounded-full border border-ponpon-navy/15 bg-white px-5 py-2 text-sm font-semibold text-ponpon-navy/70 transition hover:-translate-y-0.5 hover:border-ponpon-coral/40 hover:text-ponpon-coral focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ponpon-coral/50"
          >
            <span aria-hidden="true">←</span>
            Back to locker
          </button>
        </div>
      </div>

      {/* Interactive flow: fixed 9-slot shelf on top, items below; tap slot then tap item to place */}
      <div className="flex flex-col gap-6">
        {/* Shelf: empty at first; tap a slot then tap an item below to place */}
        <section className="flex flex-col gap-3">
          <p className="text-xs font-semibold text-ponpon-navy/80">
            {activeSlotIndex !== null
              ? 'Tap an item below to put it on the shelf'
              : 'Tap a slot to place your gachapon. Double-tap to remove it.'}
          </p>
          <div className="mx-auto w-full max-w-sm">
            {/* Shelf preview: always visible; tap a slot to select, then tap an item below */}
            <div className="w-full max-w-xs mx-auto">
              <div
                ref={previewRef}
                className={`overflow-hidden border border-ponpon-navy/15 bg-slate-50 shadow-lg ${currentFormat.aspectClass}`}
              >
                <div className="flex h-full w-full flex-col">
                  <div className="relative flex h-full w-full flex-col items-stretch justify-center px-3 pt-2 pb-6 bg-gradient-to-br from-white via-slate-50 to-slate-100">
                    {/* Three-tier shelf: 3 shelves × 3 slots each */}
                    <div className="flex h-full w-full flex-col justify-center gap-4">
                      {[0, 1, 2].map((row) => {
                        const startIndex = row * 3;
                        return (
                          <div key={row} className="relative flex flex-1 items-end justify-center">
                            <div className="relative w-full max-w-[92%]">
                              {/* Shelf image base for this row */}
                              <div className="pointer-events-none absolute inset-x-0 bottom-0 z-0 flex items-end justify-center">
                                <img
                                  src="/assets/shelf.png"
                                  alt=""
                                  className="block w-full max-w-full object-contain"
                                />
                              </div>
                              {/* Item slots aligned along the shelf, slightly above the base */}
                              <div className="absolute inset-x-0 bottom-1 z-10 flex w-full items-end justify-between gap-2">
                                {slotAssignments.slice(startIndex, startIndex + 3).map((_, offset) => {
                                  const idx = startIndex + offset;
                                  const item = selectableItems.find((x) => x.key === slotAssignments[idx]);
                                  const e = item?.entry;
                                  const img = e?.prize?.imageUrl?.trim() || e?.imageUrl?.trim() || FALLBACK_IMAGE;
                                  const isActive = activeSlotIndex === idx;
                                  return (
                                      <button
                                      key={idx}
                                      type="button"
                                      onClick={() => handleSlotClick(idx)}
                                          className="relative flex h-20 flex-1 items-end justify-center rounded-lg focus:outline-none"
                                    >
                                      {/* subtle shadow pad to indicate valid drop area */}
                                      {(e || !isExporting) && (
                                        <div className="pointer-events-none absolute inset-x-3 bottom-0 h-3 rounded-full bg-black/15 blur-sm" />
                                      )}
                                        {e ? (
                                        <div
                                          className={`flex h-14 w-14 items-center justify-center rounded-md ${
                                            isActive ? 'ring-2 ring-ponpon-coral' : ''
                                          }`}
                                        >
                                          <img
                                            src={img}
                                            alt=""
                                            className="max-h-full max-w-full object-contain"
                                            style={{
                                              filter:
                                                'drop-shadow(0 2px 3px rgba(0,0,0,0.35)) drop-shadow(0 6px 10px rgba(0,0,0,0.3))',
                                            }}
                                            loading="lazy"
                                            decoding="async"
                                          />
                                        </div>
                                      ) : (
                                        !isExporting && (
                                          <div
                                            className={`flex h-14 w-14 items-center justify-center rounded-md border border-dashed border-ponpon-navy/25 bg-white/40 text-[10px] font-medium text-ponpon-navy/40 ${
                                              isActive ? 'ring-2 ring-ponpon-coral' : ''
                                            }`}
                                          >
                                            + Add
                                          </div>
                                        )
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CHANGED: Move download button between preview and My Gachapon */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-1 flex-col items-center justify-center gap-4">
            <div className="flex w-full flex-col items-center gap-3">
              <button
                type="button"
                onClick={handleDownload}
                disabled={isExporting || selectedItems.length === 0}
                // CHANGED: bump CTA size to match primary buttons (e.g. LockerPage)
                className={`mt-1 inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold shadow-[0_10px_24px_rgba(15,23,42,0.25)] transition ${
                  isExporting || selectedItems.length === 0
                    ? 'cursor-not-allowed bg-ponpon-navy/20 text-ponpon-navy/40 shadow-none'
                    : 'bg-ponpon-coral text-white hover:-translate-y-0.5 hover:bg-[#E23A32]'
                }`}
              >
                {/* CHANGED: CTA label from 'Download' to 'Share Now' */}
                {isExporting ? 'Preparing…' : 'Share Now'}
              </button>
            </div>
          </div>
        </div>

        {/* CHANGED: rename section label from 'Your Collections' to 'Your Gachapon' */}
        <section className="flex flex-col gap-3">
            <p className="text-xs font-semibold text-ponpon-navy/80">Your Gachapon</p>
            {selectableItems.length === 0 ? (
              <p className="rounded-xl border border-dashed border-ponpon-navy/20 bg-ponpon-navy/5 p-4 text-center text-xs text-ponpon-navy/60">
                No items to showcase yet. Spin a machine to get your first figure.
              </p>
            ) : availableSelectableItems.length === 0 ? (
              <p className="rounded-xl border border-dashed border-ponpon-navy/20 bg-ponpon-navy/5 p-4 text-center text-xs text-ponpon-navy/60">
                All items are on the shelf. Clear a slot to add more.
              </p>
            ) : (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {availableSelectableItems.map((item) => {
                  const img = item.prizeImg || FALLBACK_IMAGE;
                  const name = item.prizeName || 'Item';
                  return (
                    <button
                      key={item.key}
                      type="button"
                      disabled={activeSlotIndex === null}
                      onClick={() => {
                        if (activeSlotIndex !== null) {
                          handleSlotPick(activeSlotIndex, item.key);
                        }
                      }}
                      className={`flex flex-col items-center gap-1 rounded-xl border bg-white p-2 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-ponpon-coral/50 ${
                        activeSlotIndex === null
                          ? 'cursor-not-allowed border-ponpon-navy/10 opacity-60'
                          : 'border-ponpon-navy/10 hover:-translate-y-0.5 hover:border-ponpon-coral/40'
                      }`}
                    >
                      <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg">
                        <img
                          src={img}
                          alt={name}
                          className="max-h-full max-w-full object-contain"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                      <span className="line-clamp-1 text-[10px] text-ponpon-navy/80">{name}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
      </div>
    </div>

    {/* In-page modal: show exported image on iOS (long-press to save); no popup = stable */}
    {exportedImageDataUrl && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
        role="dialog"
        aria-modal="true"
        aria-label="Exported PonDisplay image"
        onClick={() => setExportedImageDataUrl(null)}
      >
        <div
          className="flex max-h-full max-w-full flex-col items-center gap-4 rounded-2xl bg-white p-4 shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={exportedImageDataUrl}
            alt="Your PonDisplay"
            className="max-h-[70vh] w-auto max-w-full rounded-lg object-contain"
          />
          <p className="text-center text-sm text-ponpon-navy/80">
            Long press the image to save to your photos.
          </p>
          <button
            type="button"
            onClick={() => setExportedImageDataUrl(null)}
            className="rounded-full bg-ponpon-coral px-5 py-2 text-sm font-semibold text-white hover:bg-[#E23A32]"
          >
            Close
          </button>
        </div>
      </div>
    )}
    </>
  );
};

export default LockerShowcasePage;

